import { default as MenuIcon } from '@mui/icons-material/Menu'
import AccountCircle from '@mui/icons-material/AccountCircle'
import Logout from '@mui/icons-material/Logout'
import Settings from '@mui/icons-material/Settings'
import Notifications from '@mui/icons-material/Notifications'
import Add from '@mui/icons-material/Add'
import Business from '@mui/icons-material/Business'
import SwapHoriz from '@mui/icons-material/SwapHoriz'
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  Chip,
  Badge,
  Tooltip,
  ButtonBase,
} from '@mui/material'
import { useContext, useEffect, useState, useMemo } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { ClinicLayoutContext } from 'src/clinic/context/ClinicLayoutProvider'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { logoutHandlerMentor, getImageLink, isValidHttpUrl } from 'src/mentor/helpers/utilityFunctions'
import { mediaURL } from 'src/consumer/services'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { HeaderContainer, HeaderGreeting, HeaderLinkText } from './ClinicHeaderStyles'
import PortalSwitcherButton from './PortalSwitcherButton'
import CommsQuotaDialog from 'src/clinic/components/CommsQuotaDialog'
import { getClinicBrandCommsQuota } from 'src/clinic/services/commsQuotaService'
import BrandSwitcherDrawer from './BrandSwitcherDrawer'
import ClinicAssistantProfileDrawer from '../ClinicAssistantProfileDrawer'
import ClinicAssistantProfileMenuItem from '../ClinicAssistantProfileDrawer/ClinicAssistantProfileMenuItem'

// Styled components for modern header
const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    gap: 12,
  },
}))

const HeaderRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  [theme.breakpoints.down('sm')]: {
    gap: 8,
  },
}))

const MenuButton = styled(IconButton)(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.04) 100%)',
  border: '1px solid rgba(116, 80, 170, 0.12)',
  color: '#7450aa',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.08) 100%)',
    borderColor: 'rgba(116, 80, 170, 0.25)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(116, 80, 170, 0.2)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
    borderRadius: '10px',
  },
}))

const GreetingContainer = styled(GenericDisplayFlexColumn)(({ theme }) => ({
  gap: 4,
  [theme.breakpoints.down('sm')]: {
    gap: 2,
  },
}))

const HeadingContainer = styled(GenericDisplayFlexColumn)(({ theme }) => ({
  gap: 6,
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    gap: 4,
  },
}))

const ProfileButton = styled(ButtonBase)(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: '12px',
  padding: 2,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(116, 80, 170, 0.25)',
    borderColor: 'rgba(116, 80, 170, 0.2)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
    borderRadius: '10px',
  },
}))

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: '100%',
  height: '100%',
  background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
  border: '2px solid rgba(255, 255, 255, 0.9)',
  boxShadow: '0 2px 8px rgba(116, 80, 170, 0.3)',
  fontWeight: 700,
  fontSize: 16,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(116, 80, 170, 0.4)',
    transform: 'scale(1.05)',
  },
}))

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    marginTop: 8,
    minWidth: 280,
    maxWidth: 320,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(116, 80, 170, 0.1)',
    overflow: 'visible',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 20,
      width: 12,
      height: 12,
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
      border: '1px solid rgba(116, 80, 170, 0.1)',
      borderBottom: 'none',
      borderRight: 'none',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
    },
  },
}))

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  padding: '12px 20px',
  borderRadius: '10px',
  margin: '4px 8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.04) 100%)',
  },
  '&:first-of-type': {
    marginTop: 8,
  },
  '&:last-of-type': {
    marginBottom: 8,
  },
}))

const QuotaSection = styled(Box)(({ theme }) => ({
  padding: '16px 20px',
  margin: '8px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.05) 0%, rgba(116, 80, 170, 0.02) 100%)',
  border: '1px solid rgba(116, 80, 170, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(116, 80, 170, 0.05) 100%)',
    borderColor: 'rgba(116, 80, 170, 0.2)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(116, 80, 170, 0.15)',
  },
}))

const QuotaLabel = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  color: 'rgba(116, 80, 170, 0.7)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 10,
  display: 'block',
}))

const QuotaChip = styled(Chip)(({ theme, variant }) => ({
  height: 26,
  fontSize: '11px',
  fontWeight: 600,
  borderRadius: '8px',
  border: '1px solid',
  ...(variant === 'wa'
    ? {
        backgroundColor: 'rgba(16, 185, 129, 0.12)',
        color: '#059669',
        borderColor: 'rgba(16, 185, 129, 0.3)',
      }
    : {
        backgroundColor: 'rgba(239, 68, 68, 0.12)',
        color: '#dc2626',
        borderColor: 'rgba(239, 68, 68, 0.3)',
      }),
}))

const RoleSwitchText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#6b7280',
  marginTop: 8,
  '& .link': {
    color: '#7450aa',
    cursor: 'pointer',
    fontWeight: 600,
    textDecoration: 'underline',
    textDecorationColor: 'rgba(116, 80, 170, 0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#5a3d88',
      textDecorationColor: 'rgba(116, 80, 170, 0.5)',
    },
  },
}))

const BrandSwitcherContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.04) 100%)',
  border: '1px solid rgba(116, 80, 170, 0.12)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.12) 0%, rgba(116, 80, 170, 0.06) 100%)',
    borderColor: 'rgba(116, 80, 170, 0.2)',
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 10px',
    gap: 6,
  },
}))

const BrandNameText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#7450aa',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
  },
}))

const BrandSwitcherIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: '#7450aa',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'rotate(90deg)',
  },
}))

export default function ClinicHeader({
  greetingsEnabled,
  headerLinks,
  heading,
  subHeading,
  component,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { clinicBrandId } = useParams()
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const { toggleSidebar } = useContext(ClinicLayoutContext)
  
  // Hide hamburger menu for clinic-home and ca-home routes
  const shouldHideMenuButton = location?.pathname?.startsWith('/clinic-home') || location?.pathname?.startsWith('/ca-home') || location?.pathname?.startsWith('/kiosk')
  const { ownerData, assistantData } = useContext(ClinicDataContext) || {}
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false)
  const [quotaSummary, setQuotaSummary] = useState({ waCredits: null, outcallCredits: null })
  const [quotaLoading, setQuotaLoading] = useState(false)
  const [brandSwitcherOpen, setBrandSwitcherOpen] = useState(false)
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  // Get clinic brands from context
  const clinicBrands = useMemo(() => {
    if (!ownerData?.associatedClinicBrandsData) return []
    return ownerData.associatedClinicBrandsData.map((brand) => ({
      id: brand.id,
      title: brand.title || brand.brandName || brand.clinicBrandName,
      brandName: brand.brandName || brand.clinicBrandName,
      clinicBrandName: brand.clinicBrandName || brand.brandName,
    }))
  }, [ownerData?.associatedClinicBrandsData])

  // Get current brand
  const currentBrand = useMemo(() => {
    if (!clinicBrandId || !clinicBrands.length) return null
    return clinicBrands.find((brand) => brand.id === clinicBrandId) || null
  }, [clinicBrandId, clinicBrands])

  // Check if we're on a brand page (has clinicBrandId in URL)
  const isBrandPage = Boolean(clinicBrandId)

  const isClinicOwnerOrAdmin = getAllRolesInBoolean().isClinicOwner || getAllRolesInBoolean().isClinicAdmin
  const isClinicAssistant = getAllRolesInBoolean().isClinicAssistant

  // Get profile picture and name with fallback logic
  const profileData = useMemo(() => {
    const userProfile = ownerData?.userProfileData || assistantData?.userProfileData
    const profilePicId = userProfile?.profilePicId || userProfile?.profilePictureId
    const name = userProfile?.name || userProfile?.firstName || (isClinicAssistant ? 'Clinic Assistant' : 'Clinic Manager')
    
    // Get profile picture URL with fallback
    let profilePicUrl = null
    if (profilePicId) {
      if (isValidHttpUrl(profilePicId)) {
        profilePicUrl = profilePicId
      } else {
        profilePicUrl = `${mediaURL}${profilePicId}`
      }
    }
    
    // Get initials for fallback
    const getInitials = (nameStr) => {
      if (!nameStr || (nameStr === 'Clinic Manager' && !isClinicAssistant) || (nameStr === 'Clinic Assistant' && isClinicAssistant)) {
        return isClinicAssistant ? 'CA' : 'CM'
      }
      const words = nameStr.trim().split(/\s+/).filter(w => w.length > 0)
      if (words.length > 1) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      } else if (words.length === 1 && words[0].length > 0) {
        return words[0].charAt(0).toUpperCase()
      }
      return isClinicAssistant ? 'CA' : 'CM'
    }
    
    return {
      profilePicUrl,
      name,
      initials: getInitials(name),
    }
  }, [ownerData?.userProfileData, assistantData?.userProfileData, isClinicAssistant])

  // Fetch quota summary for first brand
  useEffect(() => {
    if (isClinicOwnerOrAdmin && clinicBrands.length > 0 && open) {
      const firstBrand = clinicBrands[0]
      fetchQuotaSummary(firstBrand.id)
    } else {
      setQuotaSummary({ waCredits: null, outcallCredits: null })
    }
  }, [isClinicOwnerOrAdmin, clinicBrands, open])

  const fetchQuotaSummary = async (brandId) => {
    if (!brandId) return
    setQuotaLoading(true)
    try {
      const data = await getClinicBrandCommsQuota(brandId)
      console.log('Fetched quota data:', data)
      // Ensure we're extracting numeric values correctly
      const waCredits = data?.waCredits !== undefined ? Number(data.waCredits) : null
      const outcallCredits = data?.outcallCredits !== undefined ? Number(data.outcallCredits) : null
      
      setQuotaSummary({
        waCredits,
        outcallCredits,
      })
    } catch (error) {
      console.error('Error fetching quota summary:', error)
      setQuotaSummary({ waCredits: null, outcallCredits: null })
    } finally {
      setQuotaLoading(false)
    }
  }

  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth)
  }

  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth)
    return () => window.removeEventListener('resize', updateWindowWidth)
  }, [])

  return (
    <>
      <HeaderContainer className='hide-on-print-only'>
        <HeaderLeft>
          {!shouldHideMenuButton && (
            <MenuButton onClick={toggleSidebar} aria-label="Toggle sidebar">
              <MenuIcon />
            </MenuButton>
          )}
          
          {greetingsEnabled && (
            <GreetingContainer>
              <HeaderGreeting>Greetings!</HeaderGreeting>
              <HeaderGreeting main>Clinic Manager</HeaderGreeting>
            </GreetingContainer>
          )}
          
          {(heading || subHeading || component) && (
            <HeadingContainer>
              {component && component}
              {subHeading && <HeaderGreeting>{subHeading}</HeaderGreeting>}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {heading && <HeaderGreeting main>{heading}</HeaderGreeting>}
                {isBrandPage && currentBrand && isClinicOwnerOrAdmin && (
                  <BrandSwitcherContainer onClick={() => setBrandSwitcherOpen(true)}>
                    <Business sx={{ fontSize: 18, color: '#7450aa' }} />
                    <BrandNameText>
                      {currentBrand.brandName || currentBrand.title}
                    </BrandNameText>
                    <BrandSwitcherIcon>
                      <SwapHoriz sx={{ fontSize: 18 }} />
                    </BrandSwitcherIcon>
                  </BrandSwitcherContainer>
                )}
              </Box>
              {getAllRolesInBoolean().isClinicOwner && getAllRolesInBoolean().isDoctor && (
                <RoleSwitchText>
                  Using as clinic owner,{' '}
                  <span className="link" onClick={() => navigate('/mentor-home')}>
                    Use as doctor?
                  </span>
                </RoleSwitchText>
              )}
            </HeadingContainer>
          )}
        </HeaderLeft>

        <HeaderRight>
          {headerLinks?.map((item, index) => {
            return (
              <HeaderLinkText key={index} onClick={() => navigate(item?.link)}>
                {item?.displayName}
              </HeaderLinkText>
            )
          })}

          <PortalSwitcherButton />

          <Tooltip title={profileData.name} arrow>
            <ProfileButton onClick={handleClick} aria-label="Account menu">
              <StyledAvatar src={profileData.profilePicUrl || undefined}>
                {profileData.initials}
              </StyledAvatar>
            </ProfileButton>
          </Tooltip>

          <StyledMenu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {windowWidth < 800 &&
              headerLinks?.map((headerLink, idx) => {
                return (
                  <MenuItemStyled
                    key={idx}
                    onClick={() => {
                      navigate(headerLink?.link)
                      handleClose()
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Settings sx={{ fontSize: 18, color: '#6b7280' }} />
                      <Typography variant="body2" fontWeight={500}>
                        {headerLink?.displayName}
                      </Typography>
                    </Box>
                  </MenuItemStyled>
                )
              })}

            {/* Communication Quota Summary - Only for Clinic Owners/Admins */}
            {isClinicOwnerOrAdmin && clinicBrands.length > 0 && (
              <>
                <Divider sx={{ my: 1, mx: 2 }} />
                <QuotaSection
                  onClick={(e) => {
                    e.stopPropagation()
                    setQuotaDialogOpen(true)
                    handleClose()
                  }}
                >
                  <QuotaLabel>Communication Quota</QuotaLabel>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {quotaLoading ? (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                        Loading...
                      </Typography>
                    ) : (
                      <>
                        {quotaSummary.waCredits !== null && (
                          <QuotaChip
                            variant="wa"
                            label={`WA: ${quotaSummary.waCredits}`}
                            size="small"
                          />
                        )}
                        {quotaSummary.outcallCredits !== null && (
                          <QuotaChip
                            variant="outcall"
                            label={`Outcall: ${quotaSummary.outcallCredits}`}
                            size="small"
                          />
                        )}
                      </>
                    )}
                  </Box>
                </QuotaSection>
                <Divider sx={{ my: 1, mx: 2 }} />
              </>
            )}

            {/* Switch Clinic Brand - Show on brand pages for clinic owners */}
            {isClinicOwnerOrAdmin && isBrandPage && clinicBrands.length > 0 && (
              <>
                <MenuItemStyled
                  onClick={() => {
                    setBrandSwitcherOpen(true)
                    handleClose()
                  }}
                  sx={{
                    color: '#7450aa',
                    '&:hover': {
                      background: 'rgba(116, 80, 170, 0.08)',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <SwapHoriz sx={{ fontSize: 18, color: '#7450aa' }} />
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#7450aa' }}>
                      Switch Clinic Brand
                    </Typography>
                  </Box>
                </MenuItemStyled>
                <Divider sx={{ my: 1, mx: 2 }} />
              </>
            )}

            {/* Add Clinic Brand - Only show on landing page for clinic owners */}
            {isClinicOwnerOrAdmin && (() => {
              const pathname = location?.pathname || ''
              // Check if we're on the exact landing page (no brand ID in path)
              const pathSegments = pathname.split('/').filter(Boolean)
              const isLandingPage = pathSegments.length === 1 && pathSegments[0] === 'clinic-home'
              return isLandingPage
            })() && (
              <>
                <MenuItemStyled
                  onClick={() => {
                    navigate('/clinic-home/new-brand-onboarding')
                    handleClose()
                  }}
                  sx={{
                    color: '#7450aa',
                    '&:hover': {
                      background: 'rgba(116, 80, 170, 0.08)',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Add sx={{ fontSize: 18, color: '#7450aa' }} />
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#7450aa' }}>
                      Add Clinic Brand
                    </Typography>
                  </Box>
                </MenuItemStyled>
                <Divider sx={{ my: 1, mx: 2 }} />
              </>
            )}

            {/* Profile - Only for Clinic Assistant */}
            {isClinicAssistant && (
              <>
                <ClinicAssistantProfileMenuItem
                  onClick={() => {
                    setProfileDrawerOpen(true)
                    handleClose()
                  }}
                />
                <Divider sx={{ my: 1, mx: 2 }} />
              </>
            )}

            <MenuItemStyled
              onClick={() => {
                logoutHandlerMentor()
                navigate('/')
              }}
              sx={{
                color: '#ef4444',
                '&:hover': {
                  background: 'rgba(239, 68, 68, 0.08)',
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Logout sx={{ fontSize: 18, color: '#ef4444' }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: '#ef4444' }}>
                  Logout
                </Typography>
              </Box>
            </MenuItemStyled>
          </StyledMenu>
        </HeaderRight>
      </HeaderContainer>

      {/* Communication Quota Dialog */}
      {isClinicOwnerOrAdmin && (
        <CommsQuotaDialog
          open={quotaDialogOpen}
          onClose={() => setQuotaDialogOpen(false)}
          clinicBrands={clinicBrands}
        />
      )}

      {/* Brand Switcher Drawer */}
      {isClinicOwnerOrAdmin && (
        <BrandSwitcherDrawer
          open={brandSwitcherOpen}
          onClose={() => setBrandSwitcherOpen(false)}
        />
      )}

      {/* Clinic Assistant Profile Drawer */}
      {isClinicAssistant && (
        <ClinicAssistantProfileDrawer
          open={profileDrawerOpen}
          onClose={() => setProfileDrawerOpen(false)}
          clinicId={null}
          assistantData={assistantData}
        />
      )}
    </>
  )
}
